/**
 * Opens Consentmo cookie preferences from footer "Cookie Preferences" controls.
 *
 * Consentmo gates openDialog('preferences') behind visitorEligibleForCookieWidgetUi().
 * When that signal is false (common outside the configured consent region), preferences
 * silently no-ops. openDialog('notice') still shows the cookie bar via setShowCookieBar
 * without clearing stored consent — that is the safe fallback.
 *
 * Never call Consentmo.reset() / acceptAll / declineAll from this file.
 *
 * @see https://support.consentmo.com/en/article/consentmo-storefront-javascript-api-j0ef2h/
 */

const OPEN_CONFIRM_MS = 250;

/**
 * @type {Array<{name: string, available: () => boolean, open: (trigger?: HTMLElement) => void}>}
 */
const PREFERENCE_OPENERS = [
  {
    name: 'showPreferences',
    available: () => typeof window.showPreferences === 'function',
    open: (trigger) => window.showPreferences(trigger),
  },
  {
    name: 'Consentmo.openDialog(preferences)',
    available: () => Boolean(window.Consentmo) && typeof window.Consentmo.openDialog === 'function',
    open: () => window.Consentmo.openDialog('preferences'),
  },
  {
    name: 'openCookieWidgetPopup',
    available: () => typeof window.openCookieWidgetPopup === 'function',
    open: () => window.openCookieWidgetPopup(),
  },
];

function enableConsentmoPointerEvents() {
  const host = document.querySelector('csm-cookie-consent');
  if (!(host instanceof HTMLElement)) return;
  host.style.setProperty('pointer-events', 'auto', 'important');
  const wrapper = host.shadowRoot?.querySelector('#csm-wrapper, .csm-wrapper');
  if (wrapper instanceof HTMLElement) {
    wrapper.style.setProperty('pointer-events', 'auto', 'important');
  }
}

/**
 * @returns {{nodes: number, tallest: number, hasDialog: boolean}}
 */
function widgetSize() {
  const root = document.querySelector('csm-cookie-consent')?.shadowRoot;
  if (!root) return { nodes: 0, tallest: 0, hasDialog: false };

  let tallest = 0;
  const elements = root.querySelectorAll('*');
  for (const element of elements) {
    tallest = Math.max(tallest, element.getBoundingClientRect().height);
  }

  const dialog = root.querySelector(
    '.cc-settings-view, .cc-settings-dialog, .csm-dialog, .csm-ccd-panel, [role="dialog"], #cookieconsent-settings'
  );
  const dialogHeight = dialog ? dialog.getBoundingClientRect().height : 0;

  return {
    nodes: elements.length,
    tallest,
    hasDialog: Boolean(dialog && dialogHeight > 80),
  };
}

/**
 * @param {{nodes: number, tallest: number, hasDialog: boolean}} before
 * @param {{nodes: number, tallest: number, hasDialog: boolean}} after
 */
function somethingOpened(before, after) {
  return (
    after.hasDialog ||
    after.nodes > before.nodes + 20 ||
    after.tallest > before.tallest + 100
  );
}

/**
 * Show the Consentmo cookie bar without wiping consent.
 * openDialog('notice') only calls setShowCookieBar(true).
 *
 * @returns {boolean}
 */
function openNoticeBarSafely() {
  if (!window.Consentmo || typeof window.Consentmo.openDialog !== 'function') return false;
  try {
    window.Consentmo.openDialog('notice');
    enableConsentmoPointerEvents();
    return true;
  } catch (error) {
    console.warn('[ak-consentmo] openDialog(notice) threw', error);
    return false;
  }
}

/**
 * Try preference openers, then safe notice-bar fallback. Never resets consent.
 *
 * @param {HTMLElement} trigger
 */
function openCookiePreferences(trigger) {
  enableConsentmoPointerEvents();
  const before = widgetSize();

  for (const opener of PREFERENCE_OPENERS) {
    if (!opener.available()) continue;
    try {
      opener.open(trigger);
    } catch (error) {
      console.warn(`[ak-consentmo] ${opener.name} threw`, error);
      continue;
    }
  }

  enableConsentmoPointerEvents();

  // Preference APIs often return immediately while eligibility is false.
  // Confirm UI growth; if nothing appeared, open the notice bar (no consent wipe).
  window.setTimeout(() => {
    enableConsentmoPointerEvents();
    if (somethingOpened(before, widgetSize())) return;
    openNoticeBarSafely();
    window.setTimeout(enableConsentmoPointerEvents, 100);
    window.setTimeout(enableConsentmoPointerEvents, 400);
  }, OPEN_CONFIRM_MS);
}

let pendingOpen = null;

/**
 * @param {HTMLElement} trigger
 */
function openWhenApiIsReady(trigger) {
  if (pendingOpen) window.clearInterval(pendingOpen);

  const deadline = Date.now() + 4000;
  pendingOpen = window.setInterval(() => {
    if (window.Consentmo || typeof window.showPreferences === 'function') {
      window.clearInterval(pendingOpen);
      pendingOpen = null;
      openCookiePreferences(trigger);
      return;
    }

    if (Date.now() > deadline) {
      window.clearInterval(pendingOpen);
      pendingOpen = null;
      console.warn('[ak-consentmo] Consentmo API not ready; consent was not changed.');
    }
  }, 100);
}

/**
 * @param {EventTarget | null} target
 * @returns {HTMLElement | null}
 */
function cookiePreferencesTrigger(target) {
  if (!(target instanceof Element)) return null;

  const trigger = target.closest(
    '[data-consentmo-preferences], .isense-reopen-widget-link, a[href="#cookie-preferences"], a[href="#consentmo-preferences"]'
  );
  if (!(trigger instanceof HTMLElement)) return null;

  const title = trigger.textContent?.trim().toLowerCase() ?? '';
  const isCookiePreferences =
    trigger.hasAttribute('data-consentmo-preferences') ||
    trigger.classList.contains('isense-reopen-widget-link') ||
    title.includes('cookie preference');

  return isCookiePreferences ? trigger : null;
}

document.addEventListener(
  'click',
  (event) => {
    const trigger = cookiePreferencesTrigger(event.target);
    if (!trigger) return;
    enableConsentmoPointerEvents();
  },
  true
);

document.addEventListener('click', (event) => {
  const trigger = cookiePreferencesTrigger(event.target);
  if (!trigger) return;

  if (trigger.matches('a[href^="#"]')) event.preventDefault();

  enableConsentmoPointerEvents();

  if (window.Consentmo || typeof window.showPreferences === 'function') {
    openCookiePreferences(trigger);
  } else {
    openWhenApiIsReady(trigger);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;

  const target = event.target;
  if (!(target instanceof HTMLElement)) return;
  if (!target.matches('[data-consentmo-preferences], .isense-reopen-widget-link')) return;

  if (event.key !== ' ') return;

  event.preventDefault();
  enableConsentmoPointerEvents();
  if (window.Consentmo || typeof window.showPreferences === 'function') {
    openCookiePreferences(target);
  } else {
    openWhenApiIsReady(target);
  }
});
